<template>
	<div class="file-uploader">
		<button class="btn" :class="buttonClass" @click="triggerFileInput" :disabled="disabled || isUploading">
			<i :class="iconClass"></i> {{ buttonText }}
			<span v-if="isUploading" class="ml-2"> <i class="fas fa-spinner fa-spin"></i> Subiendo... {{ progress.toFixed(0) }}% </span>
		</button>
		<input ref="fileInput" type="file" :accept="acceptTypes" style="display: none" @change="handleFileChange" />
	</div>
	<VaModal v-model="showModalDownloadTemplate" blur title="Seleccione la carrera del archivo" hideDefaultActions @update:modelValue="handleModalClose">
		<VaCard>
			<VaCardContent>
				<VaList>
					<VaListItem v-for="(carrera, index) in carreras" :key="index">
						<VaListItemSection v-for="revoe in carrera.Revoes" :key="revoe.idAutorizacionReconocimiento">
							<div v-for="(plan, indexPlan) in revoe.Planes" :key="indexPlan" @click="selectCareer(carrera.idCarrera.toString(), revoe.numero, indexPlan)" class="career-item">
								<VaListItemLabel class="career-title">
									{{ CIdNivelEstudios[getNivelEstudios(carrera.idNivelEstudios)].toUpperCase() }}{{ ' ' + (carrera.conectorCarreraNivel || conectorCarreraNivel).toUpperCase() || ' EN ' }}
									{{ carrera.nombreCarrera.toLocaleUpperCase() }}
								</VaListItemLabel>
								<VaListItemLabel caption class="career-subtitle"> Plan: {{ plan.clavePlan }} </VaListItemLabel>
								<VaListItemLabel caption class="career-subtitle"> Tipo de carrera: {{ NOMBRE_TIPO_CARRERA[plan.tipoCarrera as ETipoCarrera] || 'Sin Tipo de Carrera' }} </VaListItemLabel>
							</div>
						</VaListItemSection>
					</VaListItem>
				</VaList>
			</VaCardContent>
		</VaCard>
	</VaModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { getAuth } from 'firebase/auth'
import { CIdNivelEstudios, EIdNivelEstudios, IInstitutionConfiguration, ETipoCarrera, NOMBRE_TIPO_CARRERA } from '@shared/types'
import { getInstitutionConfiguration } from './utils/functions'

const props = withDefaults(
	defineProps<{
		acceptTypes: string
		buttonText: string
		buttonClass: string
		iconClass: string
		disabled: boolean
		storagePath: string
		fileName: string
		institutionId: string
		testMode: boolean
		type: string
	}>(),
	{
		acceptTypes: '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		buttonText: 'Seleccionar archivo',
		buttonClass: 'btn-primary',
		iconClass: 'fas fa-upload',
		disabled: false,
		storagePath: '',
		fileName: '',
		institutionId: '',
		testMode: false,
		type: 'degrees'
	}
)

const emit = defineEmits(['upload-start', 'upload-complete', 'upload-error', 'upload-progress'])

const store = useStore()
const fileInput = ref<HTMLInputElement | null>(null)
const storage = getStorage()
const auth = getAuth()
const carreras = computed(() => store.state.global.selectedInstitution.Carrera.filter((carrera: any) => carrera !== undefined && carrera !== null))
const showModalDownloadTemplate = ref(false)
const selectedCareer = ref<{ idCarrera: string; revoe: any; plan: any } | null>(null)
/** Tarea de subida activa; se cancela si expira el timeout del loader */
let activeUploadTask: ReturnType<typeof uploadBytesResumable> | null = null
// Computed properties from store
const isUploading = computed(() => store.getters['loader/isUploading'])
const progress = computed(() => store.getters['loader/progress'])

// Reactive refs for async data
const institutionConfig = ref<IInstitutionConfiguration>({
	certificates: {
		decimals: 2,
		cycleMask: '',
		hasAccents: false,
		mayus: false
	},
	degree: {
		conectorCarreraNivel: '',
		hasAccents: false,
		services: {
			id: '',
			url: '',
			user: ''
		}
	}
})
const conectorCarreraNivel = ref<string>('')

// Load institution configuration on mount
onMounted(async () => {
	try {
		institutionConfig.value = await getInstitutionConfiguration(props.institutionId)
		conectorCarreraNivel.value = institutionConfig.value?.degree?.conectorCarreraNivel || ''
	} catch (error) {
		console.error('Error loading institution configuration:', error)
	}
})

// Función para activar el input de archivo
const triggerFileInput = () => {
	if (fileInput.value) {
		fileInput.value.click()
	}
}
// Función para obtener el nivel de estudios
const getNivelEstudios = (idNivelEstudios: string | number) => EIdNivelEstudios[idNivelEstudios as keyof typeof EIdNivelEstudios] || ''
// Manejar el cambio de archivo
const handleFileChange = async (event: Event) => {
	const input = event.target as HTMLInputElement
	if (input.files && input.files.length > 0) {
		const file = input.files[0]

		// Verificar el tipo de archivo
		const fileType = file.type
		const fileName = file.name.toLowerCase()

		// Verificar si es un archivo Excel
		const isExcel =
			fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')

		if (!isExcel) {
			store.dispatch('loader/setError', 'El archivo seleccionado no es un archivo Excel válido')
			emit('upload-error', new Error('El archivo seleccionado no es un archivo Excel válido'))
			return
		}

		try {
			// Iniciar el proceso de carga
			if (props.type === 'certificates') {
				showModalDownloadTemplate.value = true
				return // Salimos de la función aquí para esperar la selección de carrera
			}
			await uploadFile(file)
		} catch (error) {
			console.error('Error al iniciar la subida:', error)
			if (error instanceof Error) {
				store.dispatch('loader/setError', error.message)
				emit('upload-error', error)
			}
		}

		// Limpiar el input para permitir seleccionar el mismo archivo nuevamente (solo para tipos que no son certificates)
		if (props.type !== 'certificates' && fileInput.value) {
			fileInput.value.value = ''
		}
	}
}

// Nueva función para manejar la subida del archivo
const uploadFile = async (file: File) => {
	// Obtener token de autenticación
	const token = await auth.currentUser?.getIdToken()
	if (!token) {
		throw new Error('No se pudo obtener el token de autenticación')
	}

	// Crear referencia al archivo en Storage
	const finalFileName = props.fileName || file.name
	const storagePath = `${props.storagePath}/${finalFileName}`
	const fileRef = storageRef(storage, storagePath)

	// Avisar al padre antes de subir (para marcar status en RTDB y evitar race con el backend)
	emit('upload-start', {
		fileName: finalFileName,
		path: storagePath
	})

	// Subir archivo con seguimiento de progreso
	const uploadTask = uploadBytesResumable(fileRef, file, {
		customMetadata:
			props.type !== 'certificates'
				? {
						uid: auth.currentUser?.uid || ('' as string),
						fileName: finalFileName,
						fileType: file.type,
						institution: props.institutionId,
						testMode: props.testMode.toString()
					}
				: {
						uid: auth.currentUser?.uid || ('' as string),
						fileName: finalFileName,
						fileType: file.type,
						institution: props.institutionId,
						carrera: selectedCareer.value?.idCarrera || '',
						revoe: selectedCareer.value?.revoe || '',
						plan: selectedCareer.value?.plan || '',
						testMode: props.testMode.toString()
					}
	})
	activeUploadTask = uploadTask

	// Timeout de Storage: cancelar la tarea real (no solo el estado del loader)
	const storageTimeoutMs = props.type === 'certificates' || props.type === 'degree' ? 180000 : 60000
	store.dispatch('loader/startUpload', {
		timeoutDuration: storageTimeoutMs,
		timeoutCallback: () => {
			if (activeUploadTask) {
				activeUploadTask.cancel()
				activeUploadTask = null
			}
			emit('upload-error', new Error('La carga del archivo ha excedido el tiempo máximo permitido'))
		}
	})

	// Seguir el progreso de la subida
	uploadTask.on(
		'state_changed',
		snapshot => {
			const uploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
			store.dispatch('loader/updateProgress', uploadProgress)
			emit('upload-progress', uploadProgress)
		},
		error => {
			console.error('Error en la subida:', error)
			activeUploadTask = null
			// cancel() dispara storage/canceled; no duplicar error si ya lo manejó el timeout
			if ((error as { code?: string }).code === 'storage/canceled') {
				return
			}
			store.dispatch('loader/setError', error.message)
			emit('upload-error', error)
		},
		async () => {
			// Subida completada
			activeUploadTask = null
			const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
			store.dispatch('loader/finishUpload')
			emit('upload-complete', {
				url: downloadURL,
				fileName: finalFileName,
				fileType: file.type,
				path: storagePath
			})
		}
	)
}

const selectCareer = async (idCarrera: string, numero: string, indexPlan: string | number) => {
	showModalDownloadTemplate.value = false
	selectedCareer.value = { idCarrera, revoe: numero, plan: indexPlan }
	if (fileInput.value?.files && fileInput.value.files.length > 0) {
		await uploadFile(fileInput.value.files[0])
		// Limpiar el input después de completar la subida para certificados
		if (fileInput.value) {
			fileInput.value.value = ''
		}
	}
}

// Función para manejar cuando se cierra el modal sin seleccionar carrera
const handleModalClose = () => {
	showModalDownloadTemplate.value = false
	selectedCareer.value = null
	// Limpiar el input cuando se cierra el modal sin seleccionar
	if (fileInput.value) {
		fileInput.value.value = ''
	}
}
</script>

<style scoped>
.file-uploader {
	display: inline-block;
}

.fa-spinner {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}
.career-item {
	padding: 10px;
	cursor: pointer;
	transition: background-color 0.2s;
}

.career-item:hover {
	background-color: rgba(0, 0, 0, 0.05);
}

.career-title {
	font-weight: 500;
	margin-bottom: 4px;
}

.career-subtitle {
	color: #666;
	font-size: 0.9em;
}
</style>
